import { Fragment, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { LinkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'

import { equipmentAPI, workoutPlannerAPI } from '../../services/api'

/**
 * VH-134: make an exercise you already have available on this machine.
 *
 * The third way to cover a machine, beside authoring a new exercise and asking
 * the AI to propose one. Carry-over (Stage C2) already clones automatically,
 * but only between *twins* — the same normalised name and equipment type at
 * another location — so a machine with no twin left no path at all, and an
 * exercise used for a year had to be re-authored to work at a second gym.
 *
 * This LINKS rather than copies. One "Lat Pulldown" stays one row in the
 * library and becomes available in both places, which is why the library does
 * not accumulate a near-identical row per location.
 */
export default function AssignExerciseButton({ equipment, onAssigned }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [alreadyHere, setAlreadyHere] = useState(null)

  // Only fetched while the dialog is open: the library can be large and this
  // is a rare action on a screen that is otherwise read-only.
  const { data, isLoading } = useQuery({
    queryKey: ['exercises', 'assignable', search],
    queryFn: async () => {
      const response = await workoutPlannerAPI.getExercises({
        search_term: search || undefined,
        limit: 50,
      })
      return response.data
    },
    enabled: open,
  })

  const exercises = data?.exercises || data?.items || []

  const assign = async (exercise) => {
    setBusy(true)
    setError(null)
    setAlreadyHere(null)
    try {
      const { data: result } = await equipmentAPI.assignExercise(
        equipment.id,
        exercise.id
      )
      if (!result.created) {
        // Not an error. The same machine covered from two devices, or a second
        // click — say so plainly rather than failing.
        setAlreadyHere(exercise.name_en)
        return
      }
      setOpen(false)
      onAssigned?.(exercise)
    } catch (err) {
      setError(
        err?.response?.data?.detail || t('trainingLocations.assign.failed')
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="btn-outline btn-sm flex items-center"
        data-testid={`assign-button-${equipment.id}`}
      >
        <LinkIcon className="mr-1 h-4 w-4" />
        {t('trainingLocations.assign.action')}
      </button>

      <Transition show={open} as={Fragment}>
        <Dialog onClose={() => setOpen(false)} className="relative z-50">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
          </Transition.Child>

          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel className="w-full max-w-lg overflow-hidden rounded-xl bg-surface-container shadow-xl">
              <Dialog.Title className="border-b border-outline-variant px-4 py-3 text-base font-semibold text-gray-900">
                {t('trainingLocations.assign.title', {
                  machine: equipment.name_en,
                })}
              </Dialog.Title>

              <div className="p-4">
                <label className="relative block">
                  <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                  <input
                    type="search"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder={t('trainingLocations.assign.searchPlaceholder')}
                    className="form-input pl-10"
                    data-testid="assign-search"
                  />
                </label>

                {alreadyHere && (
                  <p
                    className="mt-3 text-sm text-amber-700"
                    role="status"
                    data-testid="assign-already"
                  >
                    {t('trainingLocations.assign.alreadyHere', {
                      exercise: alreadyHere,
                    })}
                  </p>
                )}

                {error && (
                  <p
                    className="mt-3 text-sm text-danger-600"
                    role="alert"
                    data-testid="assign-error"
                  >
                    {error}
                  </p>
                )}

                <ul className="mt-3 max-h-72 overflow-y-auto" data-testid="assign-list">
                  {isLoading && (
                    <li className="py-4 text-sm text-gray-500">
                      {t('common.loading')}
                    </li>
                  )}

                  {!isLoading && exercises.length === 0 && (
                    // The library really can be empty — a brand-new user, or a
                    // search that matches nothing. Say which, and point at the
                    // action that helps.
                    <li className="py-4 text-sm text-gray-500" data-testid="assign-empty">
                      {search
                        ? t('trainingLocations.assign.noMatches')
                        : t('trainingLocations.assign.libraryEmpty')}
                    </li>
                  )}

                  {exercises.map((exercise) => (
                    <li key={exercise.id}>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => assign(exercise)}
                        className="flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-left hover:bg-gray-100 disabled:opacity-50"
                        data-testid={`assign-option-${exercise.id}`}
                      >
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-medium text-gray-900">
                            {exercise.name_en}
                          </span>
                          {exercise.muscle_group && (
                            <span className="block text-xs text-gray-500">
                              {exercise.muscle_group}
                            </span>
                          )}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              <button
                type="button"
                onClick={() => setOpen(false)}
                className="w-full border-t border-outline-variant px-4 py-3 text-sm font-medium text-gray-600 hover:bg-gray-100"
                data-testid="assign-cancel"
              >
                {t('common.cancel')}
              </button>
            </Dialog.Panel>
          </div>
        </Dialog>
      </Transition>
    </>
  )
}
